/**
 * bulk-operations.js — pure patch builders for structural bulk ops (Polish
 * 16A2b). State in, patches out; no store, no DOM, no eventBus, so the
 * randomized permutation tests drive these directly in node.
 *
 * Contracts these builders own (constraints C2/C3/C4 in the phase plan):
 *  - Everything lands as ONE compound entry via store.applyCompound — never
 *    N calls to removeAt/insertAt/moveInArray, each of which mints its own
 *    history entry even inside a transaction.
 *  - Forward and reverse are built in FINAL apply order; pushHistoryEntry /
 *    applyCompound do not reverse their arguments (unlike commitTransaction).
 *  - No whole-array `replace` patches: History stores patch values by
 *    reference, and a whole-array replace leaves the reverse patch aliasing
 *    the live array the next splice corrupts.
 *  - Delete: forward removes DESCENDING (removals don't shift each other),
 *    reverse inserts ASCENDING (re-inserting low-to-high restores exact
 *    slots).
 *  - Duplicate: clone k of the ascending originals targets i_k + 1 + k, so
 *    every clone lands immediately after its original and N=1 is
 *    slot-identical to duplicateSection.
 *  - Move: collapse-and-shift semantics are expressed as a TARGET ORDER, then
 *    translated to `move` patches by selection-sort with a position map —
 *    O(L + Σ|i − from|), zero section payload on the wire.
 */

/** Map instanceId -> index from ONE pass (never a scan per id). */
export function indexByInstanceId(sections) {
  const map = new Map();
  (sections || []).forEach((section, index) => {
    if (section?.instanceId) map.set(section.instanceId, index);
  });
  return map;
}

/**
 * Resolve `instanceIds` against `sections`, ascending by index. Returns null
 * when ANY id is missing — the all-or-nothing revalidation contract: a bulk op
 * whose target vanished across an await must refuse, not partially apply.
 */
export function resolveAscending(sections, instanceIds) {
  const byId = indexByInstanceId(sections);
  const resolved = [];
  for (const id of instanceIds || []) {
    if (!byId.has(id)) return null;
    resolved.push({ instanceId: id, index: byId.get(id) });
  }
  resolved.sort((a, b) => a.index - b.index);
  return resolved;
}

/**
 * Build the compound delete: forward removes descending, reverse inserts
 * ascending, both in final apply order. Returns null if any id is missing.
 * @returns {{forward: object[], reverse: object[], indices: number[]} | null}
 */
export function buildBulkDeletePatches(sections, sectionsPath, instanceIds) {
  const resolved = resolveAscending(sections, instanceIds);
  if (!resolved || resolved.length === 0) return null;
  const forward = [];
  const reverse = [];
  for (const { index } of resolved) {
    // Ascending walk, but forward is assembled highest-first.
    forward.unshift({ op: 'remove', path: sectionsPath, index });
    reverse.push({
      op: 'insert',
      path: sectionsPath,
      index,
      item: structuredClone(sections[index])
    });
  }
  return { forward, reverse, indices: resolved.map((r) => r.index) };
}

/**
 * Build the compound duplicate: each clone lands immediately after its
 * original. `makeClone(section)` supplies the cloned instance (fresh
 * instanceId, button markup matched — the caller owns those policies; this
 * builder owns only geometry). Returns null if any id is missing.
 * @returns {{forward: object[], reverse: object[], cloneIds: string[]} | null}
 */
export function buildBulkDuplicatePatches(sections, sectionsPath, instanceIds, makeClone) {
  const resolved = resolveAscending(sections, instanceIds);
  if (!resolved || resolved.length === 0) return null;
  const forward = [];
  const reverse = [];
  const cloneIds = [];
  resolved.forEach(({ index }, k) => {
    const clone = makeClone(sections[index]);
    const insertIndex = index + 1 + k; // k earlier clones already shifted the array
    forward.push({ op: 'insert', path: sectionsPath, index: insertIndex, item: clone });
    // Reverse removes descending — assembled highest-first.
    reverse.unshift({ op: 'remove', path: sectionsPath, index: insertIndex });
    cloneIds.push(clone.instanceId);
  });
  return { forward, reverse, cloneIds };
}

/**
 * Collapse-and-shift nudge (Finder / VS Code): every selected id moves one
 * slot toward `direction` unless it is pinned against the edge by the ids
 * before it. {0,3} up → {0,2}: item 0 stays, item 3 still moves.
 * @param {string[]} orderIds  current document order
 * @param {Iterable<string>} selectedIds
 * @param {'up'|'down'} direction
 * @returns {string[]} the target order (may equal `orderIds` — a no-op)
 */
export function targetOrderForNudge(orderIds, selectedIds, direction) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const order = orderIds.slice();
  if (direction === 'up') {
    let wall = 0; // indices below this are pinned
    for (let i = 0; i < order.length; i += 1) {
      if (!selected.has(order[i])) continue;
      if (i === wall) {
        wall = i + 1; // pinned at the edge; the wall advances past it
        continue;
      }
      const [id] = order.splice(i, 1);
      order.splice(i - 1, 0, id);
    }
  } else {
    let wall = order.length - 1;
    for (let i = order.length - 1; i >= 0; i -= 1) {
      if (!selected.has(order[i])) continue;
      if (i === wall) {
        wall = i - 1;
        continue;
      }
      const [id] = order.splice(i, 1);
      order.splice(i + 1, 0, id);
    }
  }
  return order;
}

/**
 * Drop the selection at gap `gapIndex` (an index into the CURRENT order, as
 * the drop indicator reports it): selected ids are extracted in document
 * order and re-inserted as a block at the gap, adjusted for extracted ids
 * that sat above it. Dropping a non-contiguous selection "onto itself" can
 * still reorder — no-op detection is by comparing the returned order to the
 * input, never by inspecting the drop target.
 * @returns {string[]} the target order
 */
export function targetOrderForGap(orderIds, selectedIds, gapIndex) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const moving = orderIds.filter((id) => selected.has(id));
  if (!moving.length) return orderIds.slice();
  const rest = [];
  let insertAt = Math.max(0, Math.min(gapIndex, orderIds.length));
  orderIds.forEach((id, index) => {
    if (!selected.has(id)) {
      rest.push(id);
      return;
    }
    if (index < gapIndex) insertAt -= 1; // extracted from above the gap
  });
  insertAt = Math.max(0, Math.min(insertAt, rest.length));
  rest.splice(insertAt, 0, ...moving);
  return rest;
}

/**
 * Translate current → target order into `move` patches by selection-sort with
 * a position map. Both arrays must be permutations of each other. Returns
 * null when they are identical (ZERO history entries for a no-op drop) or not
 * permutations of one another.
 * @returns {{forward: object[], reverse: object[]} | null}
 */
export function buildMovePatchesForOrder(currentIds, targetIds, sectionsPath) {
  if (currentIds.length !== targetIds.length) return null;
  const pos = new Map();
  currentIds.forEach((id, index) => pos.set(id, index));
  if (pos.size !== currentIds.length) return null; // duplicate ids
  for (const id of targetIds) if (!pos.has(id)) return null;

  const work = currentIds.slice();
  const forward = [];
  for (let i = 0; i < targetIds.length; i += 1) {
    const id = targetIds[i];
    const from = pos.get(id);
    if (from === i) continue;
    forward.push({ op: 'move', path: sectionsPath, from, to: i });
    // Splice work[from] → work[i]; only positions in (i..from] shift, so the
    // map update is O(|from − i|), never a full re-index.
    const [moved] = work.splice(from, 1);
    work.splice(i, 0, moved);
    for (let j = i; j <= from; j += 1) pos.set(work[j], j);
  }
  if (!forward.length) return null;
  // Undo replays the inverse moves in reverse order.
  const reverse = forward
    .map((patch) => ({ op: 'move', path: sectionsPath, from: patch.to, to: patch.from }))
    .reverse();
  return { forward, reverse };
}
